const scrollDuration = 150; // 150ms

export enum ScrollDirection {
    VERTICAL = 'VERTICAL',
    HORIZONTAL = 'HORIZONTAL',
}

type IPickElementKey<T extends keyof HTMLElement> = Record<
    ScrollDirection,
    keyof Pick<HTMLElement, T>
>;

class ElementXY {
    private static scrollAmount: IPickElementKey<'scrollLeft' | 'scrollTop'> = {
        [ScrollDirection.HORIZONTAL]: 'scrollLeft',
        [ScrollDirection.VERTICAL]: 'scrollTop',
    };

    private static innerSize: IPickElementKey<'clientWidth' | 'clientHeight'> = {
        [ScrollDirection.HORIZONTAL]: 'clientWidth',
        [ScrollDirection.VERTICAL]: 'clientHeight',
    };

    private static offsetAmount: IPickElementKey<'offsetLeft' | 'offsetTop'> = {
        [ScrollDirection.HORIZONTAL]: 'offsetLeft',
        [ScrollDirection.VERTICAL]: 'offsetTop',
    };

    private static size: IPickElementKey<'offsetWidth' | 'offsetHeight'> = {
        [ScrollDirection.HORIZONTAL]: 'offsetWidth',
        [ScrollDirection.VERTICAL]: 'offsetHeight',
    };

    constructor(
        private readonly element: HTMLElement,
        private readonly direction: ScrollDirection,
    ) {}

    get scrollAmount() {
        return this.element[ElementXY.scrollAmount[this.direction]];
    }

    set scrollAmount(value: HTMLElement['scrollLeft' | 'scrollTop']) {
        this.element[ElementXY.scrollAmount[this.direction]] = value;
    }

    get innerSize() {
        return this.element[ElementXY.innerSize[this.direction]];
    }

    get offsetAmount() {
        return this.element[ElementXY.offsetAmount[this.direction]];
    }

    get size() {
        return this.element[ElementXY.size[this.direction]];
    }
}

export const scrollToTarget = (
    container: HTMLElement,
    target: HTMLElement,
    scrollDirection: ScrollDirection,
    smooth = true,
    scrollToPercentage = 50, // position in scrollbar to scroll to, default - center
    scrollWhenTargetNotVisible = false,
): (() => void) => {
    const containerXY = new ElementXY(container, scrollDirection);
    const targetXY = new ElementXY(target, scrollDirection);

    // scrolled amount
    const containerScrollAmount = containerXY.scrollAmount;

    // container inner with, only scrollable amount - no paddings and borders
    const containerSize = containerXY.innerSize;

    // target starting position
    const targetOffset = targetXY.offsetAmount;

    // target size including paddings and borders
    const targetSize = targetXY.size;

    // check if target element is fully visible inside scrollbar, if not - scroll
    if (
        scrollWhenTargetNotVisible &&
        targetOffset > containerScrollAmount &&
        targetOffset + targetSize < containerScrollAmount + containerSize
    ) {
        return () => {};
    }

    // scrollbar container X% position
    const scrollTo = containerScrollAmount + containerSize * (scrollToPercentage / 100);

    // current target element X% position
    const targetOriginPosition = targetOffset + targetSize * (scrollToPercentage / 100);

    const position = scrollTo - targetOriginPosition;
    let startScrollTime: number | null = null;
    let cancelAnimationFrameId: number;

    if (smooth) {
        const updateView = (timestamp: number): void => {
            if (!startScrollTime) {
                startScrollTime = timestamp;
            }

            const progress = timestamp - startScrollTime;

            containerXY.scrollAmount =
                containerScrollAmount - position * (progress / scrollDuration);

            if (progress < scrollDuration) {
                cancelAnimationFrameId = window.requestAnimationFrame(updateView);
            }
        };

        cancelAnimationFrameId = window.requestAnimationFrame(updateView);
    } else {
        containerXY.scrollAmount = containerScrollAmount - position;
    }

    return () => {
        window.cancelAnimationFrame(cancelAnimationFrameId);
    };
};
